import { flow, makeObservable, observable } from 'mobx';
import RootStore from '.';
import BaseStore from './BaseStore';
import { Publication, PublicationParams, PublicationResponse } from '@shared/types/publication';
import PublicationApi from '@shared/apis/PublicationApi';
import { ResponseData } from '@shared/types/http';

export default class PublicationStore extends BaseStore {
    publications: Publication[] = [];
    rankingPublications: Publication[] = [];
    newPublications: Publication[] = [];
    recentlyViewed: Publication[] = [];
    categoryPublications: Publication[] = [];
    publicationDetail: Publication | null = null;

    totalRanking: number = 0;
    totalNew: number = 0;
    totalRecentlyViewed: number = 0;
    totalCategory: number = 0;

    constructor(rootStore: RootStore) {
        super(rootStore);
        makeObservable(this, {
            publications: observable,
            rankingPublications: observable,
            newPublications: observable,
            recentlyViewed: observable,
            categoryPublications: observable,
            publicationDetail: observable,
            totalRanking: observable,
            totalNew: observable,
            totalRecentlyViewed: observable,
            totalCategory: observable,
            fetchPublications: flow.bound,
            fetchRankingPublications: flow.bound,
            fetchNewPublications: flow.bound,
            fetchRecentlyViewedPublications: flow.bound,
            fetchCategoryPublications: flow.bound,
            fetchPublicationDetail: flow.bound,
            selectPublication: flow.bound
        });
    }

    *fetchPublications(params?: PublicationParams) {
        try {
            const res: ResponseData<PublicationResponse> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.getPublications,
                params
            );

            if (res?.ok) {
                this.publications = res.data.elements || [];
                this.totalRecord = res.data.pagination.totalRecord;
                this.paging = {
                    ...this.paging,
                    pagination: {
                        pageIndex: res.data.pagination.page - 1,
                        pageSize: res.data.pagination.size
                    }
                };
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchRankingPublications(params?: Pick<PublicationParams, 'size' | 'page' | 'categoryCodes' | 'minPrice' | 'maxPrice'>) {
        try {
            const res: ResponseData<PublicationResponse> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.getTopPublications,
                params
            );

            if (res?.ok) {
                this.rankingPublications = res.data.elements || [];
                this.totalRanking = res.data.pagination.totalRecord;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchNewPublications(params?: PublicationParams) {
        try {
            const res: ResponseData<PublicationResponse> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.getPublications,
                params
            );

            if (res?.ok) {
                this.newPublications = res.data.elements || [];
                this.totalNew = res.data.pagination.totalRecord;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchRecentlyViewedPublications(params?: Pick<PublicationParams, 'size' | 'page'>) {
        try {
            const res: ResponseData<PublicationResponse> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.getRecentlyViewedPublications,
                params
            );

            if (res?.ok) {
                this.recentlyViewed = res.data.elements || [];
                this.totalRecentlyViewed = res.data.pagination.totalRecord;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchCategoryPublications(params?: PublicationParams) {
        try {
            const res: ResponseData<PublicationResponse> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.getPublications,
                params
            );

            if (res?.ok) {
                this.categoryPublications = res.data.elements || [];
                this.totalCategory = res.data.pagination.totalRecord;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *fetchPublicationDetail(id: string) {
        try {
            const res: ResponseData<Publication> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.getPublicationDetail,
                id,
                {
                    handleErrorBySelf: true
                }
            );

            if (res?.ok) {
                this.publicationDetail = res.data;
                return res.data;
            }
        } catch (error) {
            console.error(error);
        }
    }

    *selectPublication(publicationId: string | number) {
        try {
            const res: ResponseData<any> = yield this.rootStore.apiStore.call(
                PublicationApi,
                PublicationApi.selectPublication,
                publicationId
            );

            if (res?.ok) {
                return true;
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    clean() {
        super.clean();
        this.publications = [];
        this.rankingPublications = [];
        this.newPublications = [];
        this.recentlyViewed = [];
        this.categoryPublications = [];
        this.publicationDetail = null;
        this.totalRanking = 0;
        this.totalNew = 0;
        this.totalRecentlyViewed = 0;
        this.totalCategory = 0;
    }
}
